import { PrimaryAnnotatedType } from '@/components/annotated-type';
import { HorizontalBarChart } from '@/components/bar-chart/horizontal';
import { Answer, StoryQuestion, useStoryQuestion } from '@/components/microstory/question';
import { ScrollGraphic, ScrollRoot, ScrollStep, ScrollSteps } from '@/components/microstory/scroll';
import { StoryText } from '@/components/microstory/text';
import { useNumberFormatting } from '@/utils/use-number-formatting';
import { I18n } from '@lingui/core';
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useState } from 'react';

type Chapter1Data = WhereYouCallHomeData['younger_identify_as'];

export enum AnswerId {
  city = 'city',
  country = 'country',
  world = 'world',
}

export const getBarChartLabel = (i18n: I18n, key: AnswerId): string => {
  switch (key) {
    case AnswerId.city:
      return t(i18n)({
        message: 'Their city or region',
        id: 'where-you-call-home-2023.result-city',
      });
    case AnswerId.country:
      return t(i18n)({ message: 'Their country', id: 'where-you-call-home-2023.result-country' });
    case AnswerId.world:
      return t(i18n)({ message: 'The world', id: 'where-you-call-home-2023.result-world' });
  }
};

const getDataForAnswer = (i18n: I18n, data: Chapter1Data, answer: Answer | undefined) => {
  if (!answer) {
    return [
      {
        label: getBarChartLabel(i18n, AnswerId.world),
        value: data.world,
      },
    ];
  }

  switch (answer.id) {
    case AnswerId.city:
      return [
        {
          label: getBarChartLabel(i18n, AnswerId.city),
          value: data.city,
        },
      ];
    case AnswerId.country:
      return [
        {
          label: getBarChartLabel(i18n, AnswerId.country),
          value: data.country,
        },
      ];
    case AnswerId.world:
      return [
        {
          label: getBarChartLabel(i18n, AnswerId.world),
          value: data.world,
        },
      ];
  }
};

export function WhereYouCallHome2023Chapter1({ data }: { data: Chapter1Data }) {
  const { i18n } = useLingui();
  const formatPercentage = useNumberFormatting('percent');

  const { selectedAnswer, handleAnswer } = useStoryQuestion();
  const [chartVisible, setChartVisible] = useState(false);

  const answers = [
    {
      id: AnswerId.city,
      label: t({ id: 'where-you-call-home-2023.chapter-1.answer-1', message: 'My city or region' }),
    },
    {
      id: AnswerId.country,
      label: t({ id: 'where-you-call-home-2023.chapter-1.answer-2', message: 'My country' }),
    },
    {
      id: AnswerId.world,
      label: t({ id: 'where-you-call-home-2023.chapter-1.answer-3', message: 'The world' }),
    },
  ];

  return (
    <>
      <StoryQuestion
        question={t({
          message: `Which of these describes your sense of belonging best?`,
          id: 'where-you-call-home-2023.chapter-1.question',
        })}
        answerHeading={t({
          message: 'I mostly feel part of:',
          id: 'where-you-call-home-2023.chapter-1.heading',
        })}
        answers={answers}
        selectedAnswer={selectedAnswer}
        response={t({
          id: 'where-you-call-home-2023.chapter-1.response',
          message: "You're not alone!",
        })}
        onAnswer={handleAnswer}
      />

      <ScrollRoot>
        <ScrollGraphic>
          <HorizontalBarChart
            label={t({
              id: 'where-you-call-home-2023.chapter-1.chart-label',
              message: '% of young people who say they identify most with',
            })}
            formatValue={formatPercentage}
            data={getDataForAnswer(i18n, data, selectedAnswer)}
            visible={chartVisible}
          />
        </ScrollGraphic>

        <ScrollSteps offset={0.7} hasGraphic onStepEnter={() => setChartVisible(true)}>
          <ScrollStep data={getDataForAnswer(i18n, data, selectedAnswer)}>
            <StoryText>
              {selectedAnswer ? (
                <Trans id="where-you-call-home-2023.chapter-1.explanation">
                  You're with the {formatPercentage(data[selectedAnswer.id as AnswerId])} of{' '}
                  <PrimaryAnnotatedType>young people</PrimaryAnnotatedType> who feel the same way.
                </Trans>
              ) : (
                <Trans id="where-you-call-home-2023.chapter-1.unanswered">
                  {formatPercentage(data.world)} of young people say they identify most with the
                  world.
                </Trans>
              )}
            </StoryText>
          </ScrollStep>
        </ScrollSteps>
      </ScrollRoot>
    </>
  );
}
